import { Autocomplete, AutocompleteRenderInputParams, TextField } from '@mui/material'
import { HTMLAttributes } from 'react'
import { FieldPath, FieldValues, useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  NoOptionItem,
  optionsWithNone,
  optionToMenuItem,
  SelectContainer,
  SelectOption,
} from 'components/Select'
import { SelectAutocompleteProps } from './types'

export const SelectAutocomplete = <Schema extends FieldValues, Data>({
  control,
  disabled,
  disabledOptions,
  label,
  labelColor,
  name,
  onChange,
  optional,
  options,
  size,
  tooltip,
}: SelectAutocompleteProps<Schema, Data>): JSX.Element => {
  const { t } = useTranslation('components')

  const {
    field: { onChange: onChangeForm, value },
    fieldState: { error },
  } = useController<Schema>({
    control,
    name: name as FieldPath<Schema>,
  })

  const allOptions: SelectOption<Data>[] = optionsWithNone({ optional, options, t })

  return (
    <SelectContainer<Data>
      {...{
        disabled,
        error: Boolean(error),
        helperText: error?.message,
        label,
        name,
        tooltip,
      }}
    >
      <Autocomplete<SelectOption<Data>, false, true, false>
        autoHighlight
        disableClearable
        disabled={disabled}
        getOptionLabel={(option: SelectOption<Data>): string => option.label ?? ''}
        id={name}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        onChange={(_, selectedOption: SelectOption<Data>): void =>
          onChange !== undefined ? onChange(selectedOption) : onChangeForm(selectedOption)
        }
        options={allOptions}
        renderInput={(params: AutocompleteRenderInputParams): JSX.Element => {
          const EndAdornment: JSX.Element | undefined = options.find(
            (option: SelectOption<Data>): boolean => option.value === value.value,
          )?.endAdornment

          return (
            <TextField
              {...params}
              data-cy={name}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {EndAdornment}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              label={label}
            />
          )
        }}
        renderOption={(props: HTMLAttributes<HTMLLIElement>, option: SelectOption<Data>) => {
          if (allOptions.length === 0) {
            return <NoOptionItem t={t} />
          }

          const { endAdornment, label, logo, value } = option

          return optionToMenuItem<Data>({
            autocompleteOptionProps: props,
            disabledOptions,
            endAdornment,
            label,
            labelColor,
            logo,
            size,
            value,
          })
        }}
        value={value}
      />
    </SelectContainer>
  )
}
